export const restorationStages = [
  { code: 'arrival', title: 'Chegada & recepção' },
  { code: 'assessment', title: 'Avaliação técnica' },
  { code: 'disassembly', title: 'Desmontagem' },
  { code: 'bodywork', title: 'Funilaria' },
  { code: 'paint', title: 'Pintura' },
  { code: 'mechanics', title: 'Mecânica' },
  { code: 'assembly', title: 'Montagem' },
  { code: 'final-review', title: 'Revisão final' },
  { code: 'delivery', title: 'Entrega' }
] as const

export type ChecklistStatus = 'pending' | 'ok' | 'attention' | 'rejected' | 'notApplicable'
export type UserRole = 'admin' | 'manager' | 'technician' | 'customer'
export type MediaPhase = 'before' | 'during' | 'after'

export { digitsOnly, formatCep, formatCpfCnpj, formatPhone, normalizeCep, normalizeCpfCnpj, normalizePhone } from './customer-fields'
export { formatCurrency, formatDate, formatDateTime } from './formatters'

export interface VehicleSummary {
  id: string
  brand: string
  model: string
  year: number
  plate: string
}

export interface VehicleDetail extends VehicleSummary {
  vin: string | null
  color: string | null
  customer: { id: string; name: string; email: string; phone: string | null }
}

export interface WorkOrderSummary {
  id: string
  number: string
  status: string
  customer: string
  currentStage: string | null
  currentStageSequence: number | null
  stageCount: number
  progress: number
  rejectedItemCount: number
  coverMediaId: string | null
  expectedDelivery: string | null
  vehicle: VehicleSummary
}

export interface ChecklistItemResult {
  id: string
  group: string
  title: string
  status: ChecklistStatus
  note: string | null
  capturedAt: string | null
  version: number
}

export interface ChecklistStageResult {
  id: string
  code: string
  title: string
  sequence: number
  status: string
  progress: number
  completedAt: string | null
  version: number
  items: ChecklistItemResult[]
}

export interface MediaAssetSummary {
  id: string
  fileName: string
  contentType: string
  size: number
  sha256: string
  checklistItemResultId: string | null
  visibleToCustomer: boolean
  phase: MediaPhase
  kind: 'cover' | 'attachment'
  createdAt: string
}

export interface PartLine {
  id: string
  code: string
  description: string
  quantity: number
  unitPrice: number
}

export interface LaborEntry {
  id: string
  description: string
  hours: number
  hourlyRate: number
  technicianSubject: string | null
}

export interface TimelineEventSummary {
  id: string
  eventType: string
  description: string
  actorSubject: string | null
  visibleToCustomer: boolean
  occurredAt: string
}

// Corpo completo de GET /work-orders/{id} — inclui etapas com itens, peças, mão de obra e
// linha do tempo, usado pelas telas de detalhe (Veículo/Checklist no app do mecânico, OS no
// Operacional, Acompanhamento/Relatório no Portal do Cliente).
export interface WorkOrderDetail {
  id: string
  number: string
  status: string
  vehicleId: string
  vehicle: VehicleDetail
  assignedTechnicianSubject: string | null
  technicianDisplayName: string | null
  coverMediaId: string | null
  expectedDelivery: string | null
  isPublished: boolean
  version: number
  createdAt: string
  stages: ChecklistStageResult[]
  parts: PartLine[]
  labor: LaborEntry[]
  timeline: TimelineEventSummary[]
}

export interface ReportSummary {
  id: string
  revision: number
  variant: ReportVariant
  status: string
  total: number
  requestedAt: string
  generatedAt: string | null
}

export interface ReportApprovalSummary {
  id: string
  subject: string
  documentSha256: string
  ipAddress: string
  userAgent: string
  acceptedAt: string
}

export interface ReportDetail extends ReportSummary {
  workOrderId: string
  objectKey: string | null
  sha256: string | null
  approvals: ReportApprovalSummary[]
}

export interface ReportListItem {
  id: string
  workOrderId: string
  workOrderNumber: string
  vehicleBrand: string
  vehicleModel: string
  revision: number
  variant: ReportVariant
  status: string
  total: number
  requestedAt: string
  generatedAt: string | null
}

export type RepairStatus = 'draft' | 'pendingApproval' | 'approved' | 'rejected'
export type QuoteLineKind = 'part' | 'labor'
export type ReportVariant = 'summary' | 'full' | 'quote'

export interface RepairQuoteLine {
  id: string
  kind: QuoteLineKind
  description: string
  quantity: number
  unitPrice: number
  subtotal?: number
}

// Reparo proposto pelo mecânico numa etapa do checklist, com orçamento e decisão do cliente.
// Ver ADR-0011. `total` é a soma das linhas (quantity * unitPrice), projetada pela API.
export interface Repair {
  id: string
  workOrderId: string
  stageExecutionId: string
  title: string
  description: string | null
  status: RepairStatus
  customerDecisionNote: string | null
  submittedForApprovalAt: string | null
  decidedAt: string | null
  version: number
  quoteLines: RepairQuoteLine[]
  total: number
}
